import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, 
  faTag,
  faPieChart,
  faCloud
} from '@fortawesome/free-solid-svg-icons';
import { faAws } from '@fortawesome/free-brands-svg-icons';

export interface ResourceOverviewProps {
  data: any;
  loading: boolean;
}

export const ResourceOverview: React.FC<ResourceOverviewProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-48 bg-gray-200 rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  // Get AWS accounts overview
  const getAWSAccountsOverview = () => {
    if (!data.accounts?.aws || data.accounts.aws.length === 0) return [];
    
    return data.accounts.aws.map((account: any) => {
      // Filter resources by accountName to get per-account counts
      const accName = account.name;
      const accountResources = {
        ec2: data.aws?.ec2?.instances?.filter((i: any) => i.accountName === accName).length || 0,
        rds: data.aws?.rds?.instances?.filter((i: any) => i.accountName === accName).length || 0,
        s3: data.aws?.s3?.buckets?.filter((b: any) => b.accountName === accName).length || 0,
        lambda: data.aws?.lambda?.functions?.filter((f: any) => f.accountName === accName).length || 0,
        eks: data.aws?.eks?.clusters?.filter((c: any) => c.accountName === accName).length || 0
      };

      const totalResources = Object.values(accountResources).reduce((sum: number, count: number) => sum + count, 0);

      return {
        ...account,
        resources: accountResources,
        totalResources
      };
    });
  };

  // Calculate region distribution
  const getRegionDistribution = () => {
    const regionCounts: Record<string, number> = {};
    
    // Count resources by region
    [...(data.aws?.ec2?.instances || []), 
     ...(data.aws?.rds?.instances || []), 
     ...(data.aws?.s3?.buckets || []),
     ...(data.aws?.lambda?.functions || []),
     ...(data.aws?.eks?.clusters || [])].forEach(resource => {
      const region = resource.region;
      if (region) {
        regionCounts[region] = (regionCounts[region] || 0) + 1;
      }
    });

    return Object.entries(regionCounts).map(([region, count]) => ({
      name: region,
      value: count,
      percentage: ((count / Object.values(regionCounts).reduce((a, b) => a + b, 0)) * 100).toFixed(1)
    }));
  };

  // Calculate resource distribution by type
  const getResourceTypeDistribution = () => {
    return [
      { name: 'EC2', count: data.aws?.ec2?.total || 0, color: '#3B82F6' },
      { name: 'RDS', count: data.aws?.rds?.total || 0, color: '#10B981' },
      { name: 'S3', count: data.aws?.s3?.buckets?.length || 0, color: '#6366F1' },
      { name: 'Lambda', count: data.aws?.lambda?.total || 0, color: '#F59E0B' },
      { name: 'EKS', count: data.aws?.eks?.total || 0, color: '#8B5CF6' }
    ].filter(item => item.count > 0);
  };

  // Get top tags across all resources
  const getTopTags = () => {
    const tagCounts: Record<string, number> = {};
    
    [...(data.aws?.ec2?.instances || []), 
     ...(data.aws?.rds?.instances || []), 
     ...(data.aws?.s3?.buckets || []),
     ...(data.aws?.lambda?.functions || []),
     ...(data.aws?.eks?.clusters || [])].forEach(resource => {
      if (resource.tags) {
        Object.keys(resource.tags).forEach(tagKey => {
          tagCounts[tagKey] = (tagCounts[tagKey] || 0) + 1;
        });
      }
    });

    return Object.entries(tagCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([tag, count]) => ({ name: tag, count }));
  };

  const awsAccounts = getAWSAccountsOverview();
  const regionDistribution = getRegionDistribution();
  const resourceTypes = getResourceTypeDistribution();
  const topTags = getTopTags();

  return (
    <div className="space-y-3">
      {/* AWS Accounts Overview */}
      <div className="bg-white p-3 rounded border border-gray-300">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-base font-bold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faAws} className="mr-2 text-orange-500 text-sm" />
            AWS Accounts Overview
          </h4>
          <div className="text-xs text-gray-500">
            {awsAccounts.length} connected account{awsAccounts.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {awsAccounts.map((account: any, index: number) => (
            <div key={index} className="border border-gray-300 rounded p-2">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-bold text-xs text-gray-900 truncate">{account.name}</h5>
                <span className={`px-2 py-0.5 text-xs rounded ${
                  account.status === 'connected' || account.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {account.status}
                </span>
              </div>

              <div className="text-xs text-gray-500 mb-2">
                Account ID: {account.accountId}
              </div>

              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">EC2:</span>
                  <span className="font-bold">{account.resources.ec2}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">RDS:</span>
                  <span className="font-bold">{account.resources.rds}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">S3:</span>
                  <span className="font-bold">{account.resources.s3}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Lambda:</span>
                  <span className="font-bold">{account.resources.lambda}</span>
                </div>
                <div className="flex items-center justify-between col-span-2">
                  <span className="text-gray-600">EKS:</span>
                  <span className="font-bold">{account.resources.eks}</span>
                </div>
              </div>

              <div className="mt-2 pt-2 border-t border-gray-300">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span>Total Resources:</span>
                  <span className="text-blue-600">{account.totalResources}</span>
                </div>
              </div>

              {/* Missing Permissions Warning */}
              {data.aws?.accountPermissions?.[account.name]?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-orange-200 bg-orange-50 -mx-2 -mb-2 px-2 pb-2 rounded-b">
                  <div className="flex items-center text-xs font-bold text-orange-700 mb-1">
                    <span className="mr-1">⚠️</span> Missing IAM Permissions
                  </div>
                  <div className="space-y-0.5">
                    {data.aws.accountPermissions[account.name].map((perm: string, i: number) => (
                      <div key={i} className="text-xs text-orange-600 font-mono flex items-center">
                        <span className="mr-1">•</span> {perm}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {awsAccounts.length === 0 && (
            <div className="col-span-full text-center py-6 text-gray-500">
              <FontAwesomeIcon icon={faCloud} className="text-3xl mb-2" />
              <p className="text-xs">No AWS accounts connected</p>
              <p className="text-xs">Connect an AWS account to see resource overview</p>
            </div>
          )}
        </div>
      </div>

      {/* Grid Layout for other breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

        {/* Region Distribution */}
        <div className="bg-white p-3 rounded border border-gray-300">
          <h4 className="text-base font-bold text-gray-900 mb-2 flex items-center">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-blue-600 text-sm" />
            Resource Distribution by Region
          </h4>

          <div className="space-y-2 max-h-48 overflow-y-auto">
            {regionDistribution.map((region, index) => (
              <div key={index} className="flex items-center justify-between p-1.5 hover:bg-gray-50 rounded">
                <div className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <span className="font-bold text-xs text-gray-900">{region.name}</span>
                </div>
                <div className="text-xs text-gray-600">
                  <span className="font-bold">{region.value}</span>
                  <span className="ml-1 text-gray-500">({region.percentage}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Resource Type Distribution */}
        <div className="bg-white p-3 rounded border border-gray-300">
          <h4 className="text-base font-bold text-gray-900 mb-2 flex items-center">
            <FontAwesomeIcon icon={faPieChart} className="mr-2 text-purple-600 text-sm" />
            Resource Count by Type
          </h4>

          <div className="space-y-2">
            {resourceTypes.map((type, index) => {
              const maxCount = Math.max(...resourceTypes.map(t => t.count));
              const barWidth = maxCount > 0 ? (type.count / maxCount) * 100 : 0;

              return (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 flex-1">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: type.color }}
                    ></div>
                    <span className="font-bold text-xs text-gray-900 min-w-0">{type.name}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-1.5 ml-2">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          backgroundColor: type.color,
                          width: `${barWidth}%`
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-gray-900 ml-2">
                    {type.count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* Top Tags */}
      <div className="bg-white p-3 rounded border border-gray-300">
        <h4 className="text-base font-bold text-gray-900 mb-2 flex items-center">
          <FontAwesomeIcon icon={faTag} className="mr-2 text-orange-600 text-sm" />
          Most Common Resource Tags
        </h4>

        <div className="flex flex-wrap gap-2">
          {topTags.map((tag, index) => (
            <div
              key={index}
              className="flex items-center space-x-2 bg-gray-100 px-2 py-1 rounded text-xs"
            >
              <span className="font-bold text-gray-900">{tag.name}</span>
              <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded text-gray-600">
                {tag.count}
              </span>
            </div>
          ))}
          {topTags.length === 0 && (
            <div className="text-gray-500 text-xs">No tags found across resources</div>
          )}
        </div>
      </div>
    </div>
  );
};